import { useState } from "react";
import { BookOpen, Save, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { pontosAtivos, RAMOS, MOMENTOS } from "../lib/roteiro";
import { getToday } from "../utils";

// ═══════════════════════════════════════════════════════════════
// ROTEIRO — o material da gerência, do jeito que a IA vai ler
// ═══════════════════════════════════════════════════════════════
//
// Cada ponto é um assunto que a conversa precisa cobrir. O texto explicativo
// entra como REFERÊNCIA: a IA confere se o fornecedor entenderia aquilo lendo a
// conversa, não se as palavras bateram.
//
// A tela insiste nisso porque o risco é real — texto padrão vira script, script
// vira bloco colado, e bloco colado é o que faz o fornecedor parar de ler. Se o
// vendedor colar o texto daqui, a análise marca como conversa mecânica.

export default function ConfigRoteiro({ roteiro = [], setRoteiro, userName = "" }) {
  const [rascunho, setRascunho] = useState({});
  const [salvo, setSalvo] = useState("");

  const valor = (p, campo) => (rascunho[p.id]?.[campo] !== undefined ? rascunho[p.id][campo] : p[campo] ?? "");
  const mexer = (id, campo, v) => setRascunho((r) => ({ ...r, [id]: { ...(r[id] || {}), [campo]: v } }));

  const adicionar = () => {
    const id = `ponto-${Date.now().toString(36)}`;
    setRoteiro((prev) => [...prev, {
      id, ordem: (prev.length + 1) * 10, titulo: "", objetivo: "", textoPadrao: "",
      ativo: true, atualizadoEm: getToday(), atualizadoPor: userName,
    }]);
  };

  const salvar = (p) => {
    const mudou = rascunho[p.id] || {};
    setRoteiro((prev) => prev.map((x) => (x.id === p.id
      ? { ...x, ...mudou, atualizadoEm: getToday(), atualizadoPor: userName }
      : x)));
    setRascunho((r) => ({ ...r, [p.id]: undefined }));
    setSalvo(p.id);
    setTimeout(() => setSalvo(""), 2500);
  };

  const alternar = (p) => setRoteiro((prev) => prev.map((x) => (x.id === p.id ? { ...x, ativo: x.ativo === false } : x)));
  const remover = (p) => setRoteiro((prev) => prev.filter((x) => x.id !== p.id));

  const lista = [...roteiro].sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0));
  const ativos = pontosAtivos(roteiro).length;

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <BookOpen size={16} color="#0d9488" />
        <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Roteiro da abordagem</span>
        {ativos > 0 && (
          <span style={{ fontSize: 10, fontWeight: 700, color: "#0f766e", background: "#f0fdfa", padding: "2px 8px", borderRadius: 10 }}>
            {ativos} {ativos === 1 ? "ponto ativo" : "pontos ativos"}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 6, lineHeight: 1.45 }}>
        O que toda conversa precisa cobrir. A IA usa isto para conferir se o assunto ficou claro
        para o fornecedor — <strong>não</strong> se o vendedor repetiu as palavras.
      </div>
      <div style={{ fontSize: 11, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 7, padding: "8px 11px", marginBottom: 14, lineHeight: 1.5 }}>
        Escreva como se estivesse explicando para um vendedor novo, não como mensagem pronta.
        Se alguém colar este texto na conversa, a análise marca como <strong>conversa mecânica</strong> —
        bloco grande e tom de folheto são o que faz o fornecedor parar de ler e não responder.
      </div>

      {lista.length === 0 && (
        <div style={{ fontSize: 12, color: "#64748b", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 8, padding: "16px 18px", lineHeight: 1.6, marginBottom: 12 }}>
          <strong style={{ color: "#0f172a" }}>Nenhum ponto cadastrado ainda.</strong><br />
          Enquanto estiver vazio, a IA analisa a conversa como sempre analisou — qualidade do
          contato, resposta do fornecedor, próximo passo. O que ela ainda não consegue dizer é se
          o essencial do negócio foi explicado, porque ninguém escreveu qual é o essencial.<br />
          <span style={{ color: "#94a3b8" }}>
            Comece pelos assuntos que sempre aparecem: o repasse, o prazo de pagamento, como chega
            a ordem de serviço, o que a oficina precisa ter.
          </span>
        </div>
      )}

      {lista.map((p) => {
        const inativo = p.ativo === false;
        return (
          <div key={p.id} style={{ borderTop: "1px solid #f1f5f9", padding: "12px 0", opacity: inativo ? 0.55 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <input
                value={valor(p, "titulo")} onChange={(e) => mexer(p.id, "titulo", e.target.value)}
                placeholder="Assunto (ex.: Como funciona o repasse)"
                style={{ flex: 1, minWidth: 160, padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}
              />
              <input
                type="number" value={valor(p, "ordem")} onChange={(e) => mexer(p.id, "ordem", e.target.value)}
                title="Ordem" style={{ width: 58, padding: "7px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, textAlign: "center", color: "#64748b" }}
              />
              <button onClick={() => alternar(p)} title={inativo ? "Voltar a usar" : "Deixar de usar sem apagar"}
                style={btnIcone}>{inativo ? <EyeOff size={13} /> : <Eye size={13} />}</button>
              <button onClick={() => remover(p)} title="Remover" style={{ ...btnIcone, color: "#dc2626" }}><Trash2 size={13} /></button>
            </div>

            {/* Ramo, contrato e momento. São os três eixos que impedem a IA de
                cobrar a coisa errada: fluxo de oficina de um posto, taxa da
                Funai numa conversa da UFRGS, ou "não falou de taxa" numa
                primeira mensagem que ninguém respondeu. */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 7 }}>
              <select value={valor(p, "ramo") || "ambos"} onChange={(e) => mexer(p.id, "ramo", e.target.value)} style={sel} title="Para qual tipo de fornecedor">
                {RAMOS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <input
                value={valor(p, "contexto") || "geral"} onChange={(e) => mexer(p.id, "contexto", e.target.value)}
                placeholder="geral" title="Contrato ou contexto em que estes números valem (geral, funai, ufrgs, prefeituras…)"
                style={{ ...sel, width: 130 }}
              />
              <select value={valor(p, "quando") || "sempre"} onChange={(e) => mexer(p.id, "quando", e.target.value)} style={sel} title="Quando este ponto passa a ser cobrado">
                {MOMENTOS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>

            <input
              value={valor(p, "objetivo")} onChange={(e) => mexer(p.id, "objetivo", e.target.value)}
              placeholder="O que precisa ficar claro para o fornecedor (uma frase)"
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 11.5, color: "#475569", marginBottom: 7, boxSizing: "border-box" }}
            />
            <textarea
              value={valor(p, "textoPadrao")} onChange={(e) => mexer(p.id, "textoPadrao", e.target.value)}
              placeholder="Texto explicativo da gerência — a referência do que precisa ser dito"
              rows={4}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 11.5, color: "#475569", lineHeight: 1.5, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7 }}>
              <button onClick={() => salvar(p)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", borderRadius: 7, border: "none", background: salvo === p.id ? "#059669" : "#0d9488", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                <Save size={12} /> {salvo === p.id ? "Salvo" : "Salvar"}
              </button>
              {p.atualizadoEm && (
                <span style={{ fontSize: 9.5, color: "#94a3b8" }}>
                  atualizado em {p.atualizadoEm}{p.atualizadoPor ? ` por ${p.atualizadoPor}` : ""}
                </span>
              )}
              {inativo && <span style={{ fontSize: 10, color: "#b45309", fontWeight: 600 }}>fora do roteiro</span>}
            </div>
          </div>
        );
      })}

      <button onClick={adicionar} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, padding: "8px 14px", borderRadius: 7, border: "1px dashed #99f6e4", background: "#f0fdfa", color: "#0f766e", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
        <Plus size={13} /> Novo ponto do roteiro
      </button>
    </div>
  );
}

const sel = { padding: "5px 8px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 10.5, color: "#475569", fontFamily: "inherit" };

const btnIcone = { display: "flex", alignItems: "center", padding: "6px 8px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", cursor: "pointer" };
